/**
 * @ Author: Housefolios
 * @ Create Time: 2021-11-22 08:46:15
 * @ Modified by: David Helmick
 * @ Modified time: 2024-10-29 19:59:39
 * @ Description: Google map component that displays passed down properties and comps
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

// Centralized lodash imports - much cleaner!
import {
  map,
  isEmpty,
  head,
  last,
  filter,
  cloneDeep,
  get} from '@/utils/lodash'

import {
  GoogleMap,
  Marker,
  MarkerClusterer,
  Polyline,
  InfoWindow,
  StreetViewPanorama} from '@/utils/googleMapsReact'
import CustomMapMarker from '../CustomMapMarker'
import {
  Grid, Backdrop, Button, CircularProgress, Divider,  IconButton, Typography } from '@mui/material'
import { useMutation } from '@apollo/client/react'
import { UPDATE_PROPERTY } from '@/housefolios-components/PropertyContainer/mutations'
import { GET_PROPERTY } from '@/housefolios-components/PropertyContainer/queries'
import useAffiliate from '@/hooks/useAffiliate'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { currencyFormat, numberFormat } from '@/utils/number'
import { useGoogleMapsApi, GOOGLE_MAP_DARK_STYLES } from '@/utils/googleMaps'
import { useColorMode } from '@/theme/ColorModeProvider'

const mapContainerStyle = { height: '100%', width: '100%' }

const readRealeflowDateString = (date) => {
  if (!date) return ''
  const year = date.substr(0, 4)
  const month = date.substr(4, 2)
  const day = date.substr(6, 2)
  return `${year}-${month}-${day}`
}

function GoogleMapWithMarkers(props) {
  const {
    openComp,
    path = [],
    setPath,
    drawingCallback,
    customIcon} = props
  const mapRef = useRef(null)
  const { resolvedMode } = useColorMode()
  const [{ affiliate, affiliateLoading, urlAffiliate }] =
    useAffiliate({})

  const icon = useMemo(() => {
    if (typeof window === 'undefined' || !window.google?.maps) {
      return { url: '/images/SubjectProertyIcon.svg' }
    }
    return {
      url: customIcon
        ? customIcon
        : urlAffiliate
          ? get(affiliate, 'sidebar.sidebarMemberSmallImage.0.url') ||
            '/images/SubjectProertyIcon.svg'
          : '/images/SubjectProertyIcon.svg',
      scaledSize: new window.google.maps.Size(50, 50),
      origin: new window.google.maps.Point(0, 0),
      anchor: new window.google.maps.Point(25, 25)}
  }, [affiliate, customIcon, urlAffiliate])

  const [showInfoWindow, setShowInfoWindow] = useState(null)
  const [editClick, setEditClick] = useState(null)
  const [drawingMode, setDrawingMode] = useState(false)
  const [editMode, setEditMode] = useState(false)
  const [compWindow, setCompWindow] = useState(null)
  const [boundsSet, setBoundsSet] = useState(false)

  const polygon = useMemo(() => {
    if (!window.google?.maps) return null
    return new window.google.maps.Polygon({ paths: path  })
  }, [path])

  const handleMapClick = () => {
    setDrawingMode((current) => {
      const next = !current
      if (current && drawingCallback && polygon) {
        drawingCallback(polygon)
      }
      return next
    })
  }

  const handleMapMove = (e) => {
    if (!e?.latLng || !setPath) return
    const lat = e.latLng.lat()
    const lng = e.latLng.lng()
    setPath([...path, { lat, lng }])
  }

  const [updateProperty] = useMutation(UPDATE_PROPERTY)

  const fitBoundsIfNeeded = useCallback(
    (mapInstance) => {
      if (
        !mapInstance ||
        !window.google?.maps ||
        showInfoWindow ||
        boundsSet
      ) {
        return
      }
      if (props.properties.length <= 1 && isEmpty(props.comps)) return

      const bounds = new window.google.maps.LatLngBounds()
      map(props.properties, (property) => {
        const { location = {} } = property
        bounds.extend(
          new window.google.maps.LatLng({
            lat: Number(location.latitude),
            lng: Number(location.longitude),
          }),
        )
      })
      map(props.comps || [], (comp) => {
        bounds.extend(
          new window.google.maps.LatLng({
            lat: Number(
              comp.location?.latitude || comp.location?.lat,
            ),
            lng: Number(
              comp?.location?.longitude || comp?.location?.lon,
            ),
          }),
        )
      })
      map(props.potentialComps || [], (comp) => {
        bounds.extend(
          new window.google.maps.LatLng({
            lat: Number(comp.location.latitude),
            lng: Number(comp.location.longitude)}),
        )
      })
      mapInstance.fitBounds(bounds, 10)
      setBoundsSet(true)
    },
    [
      boundsSet,
      props.comps,
      props.potentialComps,
      props.properties,
      showInfoWindow,
    ],
  )

  const onMapLoad = useCallback(
    (mapInstance) => {
      mapRef.current = mapInstance
      fitBoundsIfNeeded(mapInstance)
    },
    [fitBoundsIfNeeded],
  )

  useEffect(() => {
    fitBoundsIfNeeded(mapRef.current)
  }, [fitBoundsIfNeeded])

  const mapOptions = useMemo(() => {
    const opts = {
      gestureHandling: 'cooperative',
      fullscreenControl: !setPath,
    }
    const ColorScheme = window.google?.maps?.ColorScheme
    if (ColorScheme) {
      opts.colorScheme =
        resolvedMode === 'dark' ? ColorScheme.DARK : ColorScheme.LIGHT
    } else if (resolvedMode === 'dark') {
      opts.styles = GOOGLE_MAP_DARK_STYLES
    } else {
      opts.styles = []
    }
    return opts
  }, [setPath, resolvedMode])

  if (affiliateLoading)
    return (
      <Backdrop open={true} style={{ color: '#fff', zIndex: 2000 }}>
        <CircularProgress color="inherit" />
      </Backdrop>
    )

  return (
    <GoogleMap
      key={`map-${resolvedMode}`}
      mapContainerStyle={mapContainerStyle}
      zoom={15}
      center={props.start}
      onLoad={onMapLoad}
      onClick={(event) => {
        setShowInfoWindow(null)
        if (props.propertyPage) setEditClick(event.latLng)
        if (props.setPath) {
          handleMapMove(event)
          handleMapClick()
        }
      }}
      onMouseOut={() => {
        setDrawingMode(false)
        if (drawingMode && drawingCallback && polygon) {
          drawingCallback(polygon)
        }
      }}
      onMouseMove={(event) => {
        if (drawingMode) {
          handleMapMove(event)
        }
      }}
      onIdle={() => {
        setDrawingMode(false)
        if (drawingMode && drawingCallback && polygon) {
          drawingCallback(polygon)
        }
      }}
      options={mapOptions}
    >
      <StreetViewPanorama
        options={{
          position: props.start,
          visible: Boolean(props.streetView),
          pov: {
            heading: props.heading ? props.heading : 0,
            pitch: 0}}}
        onCloseClick={
          props.setView ? () => props.setView('Map') : undefined
        }
      />
      <MarkerClusterer
        options={{
          averageCenter: true,
          enableRetinaIcons: true,
          gridSize: 10}}
      >
        {(clusterer) =>
          map(props.properties, (property, index) => {
              const { location = {} } = property
              const position = {
                lat: Number(location.latitude),
                lng: Number(location.longitude)}
              return (
                <CustomMapMarker
                  key={property._id || `map-property-${index}`}
                  icon={icon}
                  position={position}
                  clusterer={clusterer}
                  property={property}
                  propertyPage={props.propertyPage}
                  selected={props.selected}
                  selectProperty={props.selectProperty}
                  portfolios={props.portfolios}
                  marketplace={props.marketplace}
                  shared={props.shared}
                  public={props.public}
                  noInfoWindow={props.noInfoWindow}
                  showInfoWindow={showInfoWindow}
                  setShowInfoWindow={setShowInfoWindow}
                />
              )
            })}
      </MarkerClusterer>
      {editClick && head(props.properties) && (
        <Marker position={editClick} />
      )}
      {editClick && head(props.properties) && props.propertyPage && (
        <Button
          variant="contained"
          color="primary"
          style={{ position: 'absolute', left: 5, bottom: 10 }}
          onClick={() => {
            updateProperty({
              variables: {
                id: head(props.properties)._id,
                propertyInput: {
                  location: {
                    latitude: editClick.lat(),
                    longitude: editClick.lng()}}},
              refetchQueries: [
                {
                  query: GET_PROPERTY,
                  variables: { id: head(props.properties)._id }},
              ]}).then(() => {
              setEditClick(null)
            })
          }}
        >
          Save new location
        </Button>
      )}
      {!isEmpty(path) && (
        <Polyline
          path={path}
          onClick={() => handleMapClick()}
          options={{
            strokeColor: '#FF0000',
            editable: editMode,
            draggable: true,
            geodesic: true}}
        />
      )}
      {!isEmpty(path) && (
        <Polyline
          path={[head(path), last(path)]
          }onClick={() => handleMapClick()}
          options={{
            strokeColor: '#FF0000',
            geodesic: true}}
        />
      )}
      {!isEmpty(path) && (
        <Button
          variant="contained"
          color="primary"
          style={{ position: 'absolute', left: 5, bottom: 10 }}
          onClick={() => {
            setEditClick(null)
            setPath([])
          }}
        >
          Clear Line
        </Button>
      )}
      {drawingMode && (
        <Button
          variant="contained"
          style={{ position: 'absolute', right: 60, top: 10 }}
        >
          Drawing - Click Mouse to Stop
        </Button>
      )}
      {!isEmpty(props.comps) &&
        map(props.comps, (compObj, index) => {
          let comp = cloneDeep(compObj)
          const loc = {
            latitude: comp.location?.latitude || comp.location?.lat,
            longitude:
              comp?.location?.longitude || comp?.location?.lon}
          const point = new window.google.maps.LatLng({
            lat: Number(loc.latitude),
            lng: Number(loc.longitude)})

          const isInside = window.google.maps.geometry
            ? window.google.maps.geometry.poly.containsLocation(
                point,
                polygon,
              )
            : false

          comp.indexTest = (index + 1).toString()
          return (
            <Marker
              position={{
                lat: Number(loc.latitude),
                lng: Number(loc.longitude)}}
              label={{
                text: (index + 1).toString(),
                color: 'white',
                fontSize: '20px'}}
              icon={{
                url: '/images/compsBorder.svg',
                scaledSize: new window.google.maps.Size(50, 50),
                origin: new window.google.maps.Point(0, 0),
                anchor: new window.google.maps.Point(25, 25)}}
              key={comp?._id || `comp-${index}`}
              onClick={
                props.compWindow
                  ? () => setCompWindow(comp?._id)
                  : () => openComp(comp)
              }
            >
              {compWindow === comp?._id && props.compWindow && (
                <InfoWindow
                  position={{
                    lat: Number(loc.latitude),
                    lng: Number(loc.longitude),
                  }}
                  onCloseClick={() => setCompWindow(null)}
                >
                  <Grid
                    container
                    direction={'column'}
                    onClick={(event) => event.stopPropagation()}
                  >
                    <Grid
                      container
                      direction={'row'}
                      sx={{
                        justifyContent: 'space-between'
                      }}
                    >
                      <Grid>
                        <Typography>
                          {comp?.address_number +
                            ' ' +
                            comp?.address_street +
                            ' ' +
                            (comp?.address_suffix || '')}
                        </Typography>
                        <Typography>
                          {comp?.address_city}, {comp?.address_state}
                        </Typography>
                      </Grid>
                      <Grid>
                        <IconButton
                          onClick={() => {
                            window.open(
                              `https://www.google.com/search?q=${
                                comp?.address_number +
                                ' ' +
                                comp?.address_street +
                                ' ' +
                                (comp?.address_suffix || '')
                              }`,
                              '_blank',
                            )
                          }}
                        >
                          <FontAwesomeIcon
                            icon={['fas', 'magnifying-glass']}
                          />
                        </IconButton>
                      </Grid>
                    </Grid>
                    <Grid container direction={'row'}>
                      <Grid>
                        {numberFormat.format(comp?.sum_building_sq_ft)}{' '}
                        sqft * {comp?.bedrooms} bed *{' '}
                        {comp?.bath_full} baths *{' '}
                        {comp?.LookupCodes?.PropertyType}
                      </Grid>
                    </Grid>
                    <Divider />
                    <Grid
                      container
                      direction={'row'}
                      spacing={1}
                    >
                      <Grid>
                        <b>
                          {currencyFormat.format(
                            comp?.status_info?.price_sold,
                          )}
                        </b>
                      </Grid>
                      <Grid>
                        {readRealeflowDateString(
                          comp?.status_info?.date_sold,
                        )}
                      </Grid>
                    </Grid>
                  </Grid>
                </InfoWindow>
              )}
            </Marker>
          );
        })}
      {!isEmpty(props.potentialComps) &&
        map(props.potentialComps, (comp, index) => {
          const loc = comp.location
          return (
            <Marker
              position={{
                lat: Number(loc.latitude),
                lng: Number(loc.longitude)}}
              label={{
                text: (index + 1).toString(),
                color: 'white',
                fontSize: '20px'}}
              icon={{
                url: '/images/potentialCompsBorder.svg',
                scaledSize: new window.google.maps.Size(50, 50),
                origin: new window.google.maps.Point(0, 0),
                anchor: new window.google.maps.Point(25, 25)}}
              key={comp?._id || `potential-${index}`}
              onClick={() => openComp(comp)}
            />
          )
        })}
    </GoogleMap>
  );
}

export default function PropertiesMapComponent(props) {
  const { isLoaded } = useGoogleMapsApi()
  const [heading, setHeading] = useState()

  const { comps = [], potentialComps = [] } = props

  const { latitude = 41.850033, longitude = -87.6500523 } = !isEmpty(
    props.properties,
  )
    ? props.properties[0].location || {}
    : {}

  const start = { lat: Number(latitude), lng: Number(longitude) }

  useEffect(() => {
    if (!isLoaded || heading || !window.google?.maps) return
    const service = new window.google.maps.StreetViewService()
    service.getPanoramaByLocation(start, 50, function (panoData) {
      if (panoData !== null) {
        var ManLatLng = panoData.location.latLng
        setHeading(
          window.google.maps.geometry.spherical.computeHeading(
            ManLatLng,
            new window.google.maps.LatLng(
              Number(latitude),
              Number(longitude),
            ),
          ),
        )
      }
    })
  }, [heading, isLoaded, latitude, longitude, start.lat, start.lng])

  if (!isLoaded) {
    return (
      <div
        className={props.className}
        style={{
          height: props.height ? props.height : '525px',
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'}}
      >
        <CircularProgress size={28} />
      </div>
    )
  }

  return (
    <>
      <div
        className={props.className}
        style={{
          height: props.height ? props.height : '525px',
          width: '100%'}}
      >
        {!props.hide && (
          <GoogleMapWithMarkers
            start={start}
            streetView={props.streetView}
            heading={heading}
            comps={comps}
            potentialComps={potentialComps}
            properties={filter(props.properties, (property) => {
              return !isEmpty(property.location)
            })}
            openComp={props.openComp}
            propertyPage={props.propertyPage}
            path={props.path}
            setPath={props.setPath}
            drawingCallback={props.drawingCallback}
            shared={props.shared}
            public={props.public}
            noInfoWindow={props.noInfoWindow}
            compWindow={props.compWindow}
            customIcon={props.customIcon}
          />
        )}
      </div>
    </>
  )
}
